import React, { useState, useEffect } from 'react'
import { Smartphone, AlertTriangle, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
} from '../ui'
import { LoadingSpinner } from '../ui/loading-states'
import { apiClient } from '../../lib/api'
import type { Employee } from '../../hooks/useEmployees'
import { APP_TIME_ZONE } from '@/utils/dateFormat'

interface BoundDevice {
  id: string
  device_id: string
  device_name: string | null
  platform: string
  app_version: string | null
  bound_at: string
  last_used_at: string | null
}

interface ResetDeviceBindingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee: Employee | null
}

export function ResetDeviceBindingDialog({
  open,
  onOpenChange,
  employee,
}: ResetDeviceBindingDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingDevice, setIsLoadingDevice] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [boundDevice, setBoundDevice] = useState<BoundDevice | null>(null)

  // Load bound device info when dialog opens
  useEffect(() => {
    if (open && employee) {
      setError(null)
      setSuccess(false)
      setBoundDevice(null)
      loadBoundDevice()
    }
  }, [open, employee])

  const loadBoundDevice = async () => {
    if (!employee) return

    setIsLoadingDevice(true)
    try {
      const response = await apiClient.getBoundDevice(employee.id)
      if (response.has_bound_device && response.device) {
        setBoundDevice(response.device)
      }
    } catch (err) {
      console.error('Failed to load bound device:', err)
    } finally {
      setIsLoadingDevice(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  const handleResetBinding = async () => {
    if (!employee) return

    setIsLoading(true)
    setError(null)

    try {
      await apiClient.resetDeviceBinding(employee.id)
      setSuccess(true)
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      } else if (typeof err === 'object' && err !== null && 'message' in err) {
        setError(String((err as { message: unknown }).message))
      } else {
        setError('Не удалось сбросить привязку устройства')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getEmployeeFullName = (emp: Employee) => {
    const parts = [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean)
    return parts.join(' ')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      timeZone: APP_TIME_ZONE,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getPlatformLabel = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'ios':
        return 'iOS (iPhone/iPad)'
      case 'android':
        return 'Android'
      default:
        return platform
    }
  }

  if (!employee) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Сброс привязки устройства
            </div>
          </DialogTitle>
          <DialogDescription>
            Сбросьте привязку устройства, чтобы сотрудник мог войти с нового телефона
          </DialogDescription>
          {!isLoading && !success && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        {!success ? (
          <>
            <div className="space-y-4 py-4">
              {/* Employee info */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-sm text-gray-600">Сотрудник</p>
                <p className="font-medium">{getEmployeeFullName(employee)}</p>
                <p className="text-sm text-gray-500">
                  Табельный номер: {employee.employee_number}
                </p>
              </div>

              {/* Bound device info */}
              {isLoadingDevice ? (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner className="h-5 w-5" />
                  <span className="ml-2 text-sm text-gray-600">
                    Загрузка информации об устройстве...
                  </span>
                </div>
              ) : boundDevice ? (
                <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                  <p className="text-sm text-blue-600 font-medium">
                    Привязанное устройство
                  </p>
                  <div className="space-y-1">
                    <p className="font-medium text-blue-900">
                      {boundDevice.device_name || 'Устройство без имени'}
                    </p>
                    <p className="text-sm text-blue-700">
                      Device ID: <span className="font-mono text-xs">{boundDevice.device_id}</span>
                    </p>
                    <p className="text-sm text-blue-700">
                      Платформа: {getPlatformLabel(boundDevice.platform)}
                    </p>
                    {boundDevice.app_version && (
                      <p className="text-sm text-blue-700">
                        Версия приложения: {boundDevice.app_version}
                      </p>
                    )}
                    <p className="text-sm text-blue-700">
                      Привязано: {formatDate(boundDevice.bound_at)}
                    </p>
                    {boundDevice.last_used_at && (
                      <p className="text-sm text-blue-700">
                        Последнее использование: {formatDate(boundDevice.last_used_at)}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    У сотрудника нет привязанного устройства
                  </p>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}

              {/* Warning */}
              {boundDevice && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">После сброса:</p>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>Сотрудник не сможет войти со старого устройства</li>
                        <li>При следующем входе новое устройство будет привязано автоматически</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Отмена
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleResetBinding}
                disabled={isLoading || !boundDevice}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    Сброс...
                  </>
                ) : (
                  'Сбросить привязку'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* Success message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800">
                      Привязка устройства сброшена!
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Сотрудник может привязать новое устройство при следующем входе
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Закрыть</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
